/**
 * AddAccountModal Component
 * 
 * Modal for adding new accounts.
 * 
 * Features:
 * - Modal overlay with focus trap
 * - Account form integration
 * - Keyboard navigation support
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useEffect, useRef } from 'react';
import { AccountForm } from './AccountForm';
import { CreateAccountPayload, AccountType } from '../../types/account.types';
import { X } from 'lucide-react';

interface AddAccountModalProps {
  onClose: () => void;
  onSubmit: (data: CreateAccountPayload) => void;
  initialType?: AccountType;
  error?: string | null;
}

export function AddAccountModal({ onClose, onSubmit, initialType, error }: AddAccountModalProps): React.ReactElement {
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);

  // Focus trap and keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Focus the first focusable element
    if (firstFocusableRef.current) {
      firstFocusableRef.current.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-account-title"
      aria-describedby="add-account-description"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all dark:bg-gray-900"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div>
              <h2 id="add-account-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Add New Account
              </h2>
              <p id="add-account-description" className="text-sm text-gray-600 dark:text-gray-400">
                Create a new financial account to track your money
              </p>
            </div>
            <button
              ref={firstFocusableRef}
              onClick={onClose}
              className="btn-ghost btn-sm rounded-md p-2"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4">
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800" role="alert">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}
            <AccountForm
              onSubmit={(data) => onSubmit(data as CreateAccountPayload)}
              onCancel={onClose}
              initialType={initialType}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
